import { G } from '@mobily/ts-belt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, IconButton } from '@mui/material';
import { useMemo, useState } from 'react';
import { FunctionField, NumberField, TextField, useTranslate } from 'react-admin';

import { cashSourcesChoices } from '@sh4res/bo-common/choices/matching_contribution';
import { DatagridTyped } from '@sh4res/bo-common/components';
import { EntryFees } from '@sh4res/bo-common/components/B2bPlans/B2bPlansTypes';
import { IdField, SelectChipField } from '@sh4res/bo-common/fields';

type PlanEntryFeeRow = EntryFees & {
  has_overrides: boolean;
};

const groupEntryFees = (entryFees: EntryFees[]): PlanEntryFeeRow[] => {
  const groups = new Map<number, EntryFees[]>();

  entryFees.forEach((fee) => {
    const group = groups.get(fee.instrument_id) ?? [];
    group.push(fee);
    groups.set(fee.instrument_id, group);
  });

  return [...groups.values()].flatMap((group) => {
    const defaults = group.filter((fee) => G.isNullable(fee.cash_source));
    const overrides = group
      .filter((fee) => G.isNotNullable(fee.cash_source))
      .sort((left, right) => (left.cash_source ?? '').localeCompare(right.cash_source ?? ''));
    const hasOverrides = overrides.length > 0;

    return [...defaults, ...overrides].map((fee) => ({
      ...fee,
      has_overrides: hasOverrides,
    }));
  });
};

const entryFeesTableSx = {
  maxWidth: '100%',
  minWidth: 0,
  overflowX: 'auto',
  tr: { height: 'auto' },
  '& .RaDatagrid-table': {
    tableLayout: 'fixed',
    width: '100%',
  },
  '& .RaDatagrid-headerCell, & .RaDatagrid-rowCell': {
    whiteSpace: 'normal',
    lineHeight: 1.2,
    fontSize: '0.75rem',
    px: 0.5,
    py: 0.75,
    overflowWrap: 'normal',
  },
  '& .column-has_overrides': { width: '5%', px: 0.25 },
  '& .column-legal_status': { width: '10%' },
  '& .column-instrument': { width: '17%', overflowWrap: 'break-word' },
  '& .column-isin': {
    width: '21%',
    px: 0.5,
    whiteSpace: 'nowrap',
  },
  '& .column-cash_source': { width: '14%' },
  '& .column-amount_pct': { width: '11%' },
  '& .column-amount_pct_company, & .column-amount_pct_employee': { width: '11%' },
  '& .column-cash_source .MuiChip-root': {
    height: 'auto',
    maxWidth: '100%',
  },
  '& .column-cash_source .MuiChip-label': {
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'unset',
  },
};

const EntryFeeOverridesToggle = ({
  isExpanded,
  onToggle,
}: {
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const translate = useTranslate();

  return (
    <IconButton
      data-testid="BoExternalB2bPlan_EntryFeeExpandOverridesIconButton"
      size="small"
      aria-expanded={isExpanded}
      aria-label={translate('resources.bo_common.accessibility.toggle')}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}>
      {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
    </IconButton>
  );
};

export const B2bPlanEntryFeesTable = ({ entryFees }: { entryFees: EntryFees[] }) => {
  const translate = useTranslate();
  const [collapsedInstrumentIds, setCollapsedInstrumentIds] = useState<number[]>([]);
  const rows = useMemo(() => groupEntryFees(entryFees), [entryFees]);
  const visibleRows = rows.filter(
    (row) => G.isNullable(row.cash_source) || !collapsedInstrumentIds.includes(row.instrument_id),
  );

  const toggleInstrument = (instrumentId: number) => {
    setCollapsedInstrumentIds((ids) =>
      ids.includes(instrumentId) ? ids.filter((id) => id !== instrumentId) : [...ids, instrumentId],
    );
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <DatagridTyped<PlanEntryFeeRow>
        testId="BoExternalB2bPlanEntryFeesTable"
        bulkActionButtons={false}
        showCard={true}
        sx={entryFeesTableSx}
        columnRegistry={{
          // eslint-disable-next-line @sh4res/jsx-id-field-for-resources -- no show page
          id: <NumberField data-testid="BoExternalB2bPlan_EntryFeeIdField" />,
          instrument_id: (
            <IdField
              reference="instruments"
              data-testid="BoExternalB2bPlan_EntryFeeInstrumentIdField"
            />
          ),
          has_overrides: (
            <FunctionField
              label={false}
              sortable={false}
              data-testid="BoExternalB2bPlan_EntryFeeExpandOverridesField"
              render={(record: PlanEntryFeeRow) =>
                G.isNullable(record.cash_source) && record.has_overrides ? (
                  <EntryFeeOverridesToggle
                    isExpanded={!collapsedInstrumentIds.includes(record.instrument_id)}
                    onToggle={() => {
                      toggleInstrument(record.instrument_id);
                    }}
                  />
                ) : null
              }
            />
          ),
          legal_status: (
            <TextField
              label="resources.bo_common.b2b.companies.saving_plans.entry_fees_table.legal_status"
              data-testid="BoExternalB2bPlan_EntryFeeLegalStatusField"
            />
          ),
          instrument: (
            <TextField
              label="resources.bo_common.b2b.companies.saving_plans.entry_fees_table.instrument"
              data-testid="BoExternalB2bPlan_EntryFeeInstrumentField"
            />
          ),
          isin: (
            <TextField
              label="resources.bo_common.b2b.companies.saving_plans.entry_fees_table.isin"
              data-testid="BoExternalB2bPlan_EntryFeeIsinField"
            />
          ),
          cash_source: (
            <SelectChipField
              choices={cashSourcesChoices}
              emptyText={translate(
                'resources.bo_common.b2b.companies.saving_plans.entry_fees_table.cash_source_default',
              )}
              label="resources.bo_common.b2b.companies.saving_plans.entry_fees_table.cash_source"
              data-testid="BoExternalB2bPlan_EntryFeeCashSourceField"
            />
          ),
          amount_pct: (
            <FunctionField
              render={(record: PlanEntryFeeRow) => `${record.amount_pct}%`}
              label="resources.bo_common.b2b.companies.saving_plans.entry_fees_table.amount_pct"
              data-testid="BoExternalB2bPlan_EntryFeeAmountPctField"
            />
          ),
          amount_pct_company: (
            <FunctionField
              render={(record: PlanEntryFeeRow) => `${record.amount_pct_company}%`}
              label="resources.bo_common.b2b.companies.saving_plans.entry_fees_table.amount_pct_company"
              data-testid="BoExternalB2bPlan_EntryFeeAmountPctCompanyField"
            />
          ),
          amount_pct_employee: (
            <FunctionField
              render={(record: PlanEntryFeeRow) => `${record.amount_pct_employee}%`}
              label="resources.bo_common.b2b.companies.saving_plans.entry_fees_table.amount_pct_employee"
              data-testid="BoExternalB2bPlan_EntryFeeAmountPctEmployeeField"
            />
          ),
        }}
        displayedColumns={[
          'has_overrides',
          'legal_status',
          'instrument',
          'isin',
          'cash_source',
          'amount_pct',
          'amount_pct_company',
          'amount_pct_employee',
        ]}
        data={visibleRows}
        total={visibleRows.length}
        isLoading={false}
        sort={{ field: 'employee_status', order: 'ASC' }}
        sortableColumns={[]}
        rowSx={(record) =>
          G.isNotNullable(record.cash_source)
            ? { bgcolor: 'action.hover', '& .column-legal_status': { pl: 2 } }
            : {}
        }
      />
    </Box>
  );
};
